import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { BehaviorSubject, Subject } from 'rxjs';
import type { ClinicWithOrthancModalityInterface } from 'src/app/components/clinics/models/admin-clinic.interface';
import type { ClinicInterface } from '../models/clinic.interface';
import { Preferences } from '../models/preferences.interface';
import type { Study } from '../models/study.interface';
import { environment } from '../../../environments/environment';
import { modeType } from '../../../environments/environment.interface';
@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  studyChangedEvent: Subject<Study> = new Subject<Study>();

  private readonly _studies: BehaviorSubject<Study[]> = new BehaviorSubject<Study[]>([]);
  private readonly _clinicId: BehaviorSubject<number | undefined> = new BehaviorSubject<number | undefined>(undefined);
  private readonly _clinic: BehaviorSubject<ClinicInterface | undefined> = new BehaviorSubject<ClinicInterface | undefined>(undefined);
  private readonly _claims: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private readonly _roles: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  private readonly _roleIds: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  private readonly _signature: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly _clinicLogo: BehaviorSubject<ClinicWithOrthancModalityInterface | undefined> = new BehaviorSubject<ClinicWithOrthancModalityInterface | undefined>(undefined);
  private readonly _userName: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  private readonly _preferences: BehaviorSubject<Preferences | undefined> = new BehaviorSubject<Preferences | undefined>(undefined);

  constructor() {
  }

  public get isScc(): boolean {
    return isScc();
  }

  public get mode(): modeType {
    return this.getModeFromEnvironment(environment.mode, window.location.host);
  }

  private getModeFromEnvironment(envMode: modeType, host: string): modeType {
    if (envMode === 'mypacs') {
      if (host.includes('scc') || host.includes('lofvet')) {
        return 'scc';
      } else if (host.includes('veokee')) {
        return 'veokee';
      } else {
        return envMode;
      }
    } else {
      return envMode;
    }
  }

  public get preferences(): Preferences | undefined {
    return this._preferences.value;
  }

  public set preferences(v: Preferences | undefined) {
    this._preferences.next(v);
  }

  public get signature(): string {
    return this._signature.value;
  }

  public set signature(v: string) {
    this._signature.next(v);
  }

  public get userName(): string | undefined {
    return this._userName.value;
  }

  public set userName(v: string | undefined) {
    this._userName.next(v);
  }

  public get clinicLogo(): ClinicWithOrthancModalityInterface | undefined {
    return this._clinicLogo.value;
  }

  public set clinicLogo(v: ClinicWithOrthancModalityInterface | undefined) {
    this._clinicLogo.next(v);
  }

  public get roles(): string[] {
    return this._roles.value;
  }

  public set roles(v: string[]) {
    this._roles.next(v);
  }

  public get roleIds(): number[] {
    return this._roleIds.value;
  }

  public set roleIds(v: number[]) {
    this._roleIds.next(v);
  }

  public get claims(): string[] {
    return this._claims.value;
  }

  public set claims(v: string[]) {
    this._claims.next(v);
  }

  public get studies(): Study[]  {
    return this._studies.value;
  }

  public set studies(value: Study[] ) {
    this._studies.next(value);
  }

  public get clinicId(): number | undefined  {
    return this._clinicId.value;
  }

  public set clinicId(value: number | undefined ) {
    this._clinicId.next(value);
  }

  public get clinic(): ClinicInterface | undefined  {
    return this._clinic.value;
  }

  public set clinic(value: ClinicInterface | undefined ) {
    this._clinic.next(value);
  }

  public get clinicObservable(): Observable<ClinicInterface | undefined>  {
    return this._clinic.asObservable();
  }

  public get studiesObservable(): Observable<Study[]> {
    return this._studies.asObservable();
  }

  public get clinicIdObservable(): Observable<number | undefined> {
    return this._clinicId.asObservable();
  }

  public get claimsObservable(): Observable<string[]> {
    return this._claims.asObservable();
  }

  public get rolesObservable(): Observable<string[]> {
    return this._roles.asObservable();
  }

  public get roleIdsObservable(): Observable<number[]> {
    return this._roleIds.asObservable();
  }

  public get signatureObservable(): Observable<string> {
    return this._signature.asObservable();
  }

  public get userNameObservable(): Observable<string | undefined> {
    return this._userName.asObservable();
  }

  public get clinicLogoObservable(): Observable<ClinicWithOrthancModalityInterface | undefined> {
    return this._clinicLogo.asObservable();
  }

  public get preferencesObservable(): Observable<Preferences | undefined> {
    return this._preferences.asObservable();
  }

  notifyStudyChanged(study: Study) {
    this.studyChangedEvent.next(study);
  }
}

export const isScc = (): boolean => environment.mode === 'scc';
